
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { TextField, Select, MenuItem, Button, Box, Paper, Fade, Collapse, IconButton, Tooltip, Chip, Stack } from '@mui/material';

import { updateFilters } from '../../redux/financeAccountSlice';
import { FormControl, InputLabel } from '@mui/material';

const FilterContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
        width: '100%',
    },
}));

const FilterHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
    },
}));

const ToggleButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: 'none',
    backgroundColor: 'rgb(0,98,105)',
    color: 'white',
    boxShadow: '0 4px 16px rgba(0, 98, 105, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: 'rgb(0,85,91)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 98, 105, 0.4)',
    },
    [theme.breakpoints.down('sm')]: {
        flex: 1,
    },
}));

const QuickActionButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: 'white',
    color: theme.palette.text.primary,
    border: '2px solid #e0e0e0',
    width: '48px',
    height: '48px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: '#f5f5f5',
        borderColor: 'rgb(0,98,105)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
}));

const CollapsibleWrapper = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    zIndex: 10,
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        position: 'relative',
        top: 'auto',
        marginTop: theme.spacing(2),
    },
}));

const FilterPanel = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: '16px',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    marginTop: theme.spacing(2),
}));

const StyledForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
    }
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: '200px',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
        minWidth: 'unset',
        width: '100%',
    }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: theme.palette.background.paper,
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root': {
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: 'none',
    minHeight: '48px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginTop: theme.spacing(1),
    }
}));

const PrimaryButton = styled(ActionButton)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        width: '100%',
    }
}));

const FilterPillsContainer = styled(Stack)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginTop: theme.spacing(1),
    minHeight: '32px',
}));

const FilterPill = styled(Chip)(({ theme }) => ({
    borderRadius: '20px',
    backgroundColor: 'rgba(0, 98, 105, 0.1)',
    color: 'rgb(0, 98, 105)',
    fontWeight: 500,
    border: '1px solid rgba(0, 98, 105, 0.3)',
    height: 'auto',
    '& .MuiChip-deleteIcon': {
        color: 'rgba(0, 98, 105, 0.6)',
        '&:hover': {
            color: 'rgb(0, 98, 105)',
        },
    },
}));

function submitSearch(event, searchFilters, updateFilters, setIsFilterOpen) {
    event.preventDefault();
    updateFilters(searchFilters);
    setIsFilterOpen(false);
};

function resetFilters(event, updateFilters, updateSearchFilters,setIsFilterOpen) {
    event.preventDefault();
    const searchFilters = { name: '', userId: 'all' };
    updateSearchFilters(searchFilters);
    updateFilters(searchFilters);
    setIsFilterOpen(false);
}

function getFilterPills(searchFilters, users) {
    const pills = [];

    if (searchFilters.name) {
        pills.push({
            id: `name-${searchFilters.name}`,
            label: `Nome: ${searchFilters.name}`,
            type: 'name',
            value: searchFilters.name
        });
    }

    if (searchFilters.userId && searchFilters.userId !== 'all') {
        const user = (users || []).find(u => u.id === searchFilters.userId);
        if (user) {
            pills.push({
                id: `user-${searchFilters.userId}`,
                label: `Responsável: ${user.name}`,
                type: 'user',
                value: searchFilters.userId
            });
        }
    }

    return pills;
}

function handleRemoveFilterPill(pill, searchFilters, updateSearchFilters, updateFiltersAction) {
    let updatedFilters = { ...searchFilters };
    
    switch (pill.type) {
        case 'name':
            updatedFilters = { ...updatedFilters, name: '' };
            break;
        case 'user':
            updatedFilters = { ...updatedFilters, userId: 'all' };
            break;
        default:
            break;
    }
    
    updateSearchFilters(updatedFilters);
    updateFiltersAction(updatedFilters);
}

function Filter() {
    const dispatch = useDispatch();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    
    // Redux state selectors
    const filterFields = useSelector(state => state.financeAccount.filterFields);
    const users = useSelector(state => state.account.users);
    
    // Redux actions
    const updateFiltersAction = (filters) => dispatch(updateFilters(filters));

    const [searchFilters, updateSearchFilters] = useState({ ...filterFields });

    const filterPills = getFilterPills(searchFilters, users);
    const hasActiveFilters = filterPills.length > 0;

    const userOptions = (users || []).map(user => <MenuItem key={user.id} value={user.id}>{`${user.name}`}</MenuItem>);

    const filterForm = (
        <Fade in={true} timeout={300}>
            <FilterPanel elevation={0}>
                <StyledForm onSubmit={event => submitSearch(event, searchFilters, updateFiltersAction, setIsFilterOpen)}>
                    <StyledFormControl>
                        <StyledTextField
                            id="name"
                            name="name"
                            label="Nome"
                            variant="outlined"
                            autoFocus
                            value={searchFilters.name}
                            onChange={event => updateSearchFilters({ ...searchFilters, name: event.target.value })}
                            placeholder="Digite o nome para buscar..."
                        />
                    </StyledFormControl>
                    
                    <StyledFormControl>
                        <InputLabel id="userId-label">Responsável</InputLabel>
                        <StyledSelect
                            labelId="userId-label"
                            id="userId"
                            value={searchFilters.userId ? searchFilters.userId : 'all'}
                            label="Responsável"
                            onChange={event => updateSearchFilters({ ...searchFilters, userId: event.target.value })}
                        >
                            <MenuItem value='all'>Todos</MenuItem>
                            {userOptions}
                        </StyledSelect>
                    </StyledFormControl>
                    
                    <ButtonGroup>
                        <PrimaryButton
                            type="submit"
                            variant="contained"
                            startIcon={<FilterListIcon />}
                        >
                            Filtrar
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            variant="outlined"
                            startIcon={<ClearIcon />}
                            onClick={event => resetFilters(event, updateFiltersAction, updateSearchFilters, setIsFilterOpen)}
                        >
                            Limpar
                        </SecondaryButton>
                    </ButtonGroup>
                </StyledForm>
            </FilterPanel>
        </Fade>
    );

    return (
        <FilterContainer>
            <FilterHeader>
                <ToggleButton
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    startIcon={<FilterListIcon />}
                    endIcon={isFilterOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    {isFilterOpen ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </ToggleButton>
                <Tooltip title="Limpar Filtros" placement="top">
                    <QuickActionButton
                        onClick={event => resetFilters(event, updateFiltersAction, updateSearchFilters,setIsFilterOpen)}
                    >
                        <ClearIcon />
                    </QuickActionButton>
                </Tooltip>
            </FilterHeader>
            
            {hasActiveFilters && (
                <FilterPillsContainer role="region" aria-label="Applied filters">
                    {filterPills.map(pill => (
                        <FilterPill
                            key={pill.id}
                            label={pill.label}
                            onDelete={() => handleRemoveFilterPill(pill, searchFilters, updateSearchFilters, updateFiltersAction)}
                            sx={{
                                py: 1.5,
                                px: 1.5,
                            }}
                        />
                    ))}
                </FilterPillsContainer>
            )}
            
            <CollapsibleWrapper>
                <Collapse in={isFilterOpen} timeout={300}>
                    {filterForm}
                </Collapse>
            </CollapsibleWrapper>
        </FilterContainer>
    );
}

export default Filter;